// Report PDFs used to render synchronously inside the API request itself
// (Playwright/Chromium + a full Jinja2 pass over potentially thousands of
// rows, blocking that whole worker process). Under real concurrent load -
// several users opening a big report at once - that took the whole API
// container down. Report generation now runs as a background Celery job:
// this helper posts the job, polls its status, and resolves to the final
// PDF url once it is done. Every UI entry point that triggers a report
// (Report Designer Live Preview, PrintButton, ReportSelectionModal)
// should go through this instead of expecting an immediate PDF response.
import api from '../components/axiosConfig';

const POLL_INTERVAL_MS = 1200;
const POLL_TIMEOUT_MS = 90000;

const LOADING_TEXT = {
  en: { title: 'Generating Report', subtitle: 'This may take a few moments', dir: 'ltr' },
  ar: { title: 'جارٍ إنشاء التقرير', subtitle: 'قد يستغرق هذا بضع لحظات', dir: 'rtl' },
  fr: { title: 'Génération du rapport', subtitle: 'Cela peut prendre quelques instants', dir: 'ltr' },
  de: { title: 'Bericht wird erstellt', subtitle: 'Dies kann einen Moment dauern', dir: 'ltr' },
  it: { title: 'Generazione del report', subtitle: 'Potrebbe richiedere qualche istante', dir: 'ltr' },
  es: { title: 'Generando el informe', subtitle: 'Esto puede tardar unos momentos', dir: 'ltr' },
  zh: { title: '正在生成报告', subtitle: '这可能需要一些时间', dir: 'ltr' },
  ja: { title: 'レポートを生成中', subtitle: 'しばらく時間がかかる場合があります', dir: 'ltr' },
  ko: { title: '보고서 생성 중', subtitle: '잠시 시간이 걸릴 수 있습니다', dir: 'ltr' },
  ru: { title: 'Формирование отчёта', subtitle: 'Это может занять некоторое время', dir: 'ltr' },
  ur: { title: 'رپورٹ تیار کی جا رہی ہے', subtitle: 'اس میں چند لمحے لگ سکتے ہیں', dir: 'rtl' },
};

// 🎯 صفحة تحميل مهندمة (سبينر + progress bar غير محدد) بتتفتح فورًا وقت
// الكليك (شوف الشرح في runReportJob تحت) بدل about:blank فاضية - بتترجم
// حسب لغة الواجهة الحالية (lang بييجي من i18n.language عند نقطة الاستدعاء،
// مش من جوه الصفحة نفسها، عشان نضمن نفس اللغة المعروضة للمستخدم فعليًا)
export function buildLoadingPageHtml(lang) {
  const copy = LOADING_TEXT[lang] || LOADING_TEXT.en;
  return `<!DOCTYPE html>
<html dir="${copy.dir}" lang="${lang}">
<head>
<meta charset="UTF-8">
<title>${copy.title}…</title>
<style>
  * { box-sizing: border-box; }
  body {
    margin: 0; min-height: 100vh; display: flex; align-items: center; justify-content: center;
    background: linear-gradient(160deg, #f4f7fb 0%, #eef2f8 100%);
    font-family: 'Segoe UI', Tahoma, Arial, sans-serif;
  }
  .card {
    background: #fff; border-radius: 16px; padding: 48px 56px; box-shadow: 0 20px 60px rgba(26,51,83,0.12);
    text-align: center; max-width: 380px;
  }
  .spinner {
    width: 56px; height: 56px; margin: 0 auto 24px; border-radius: 50%;
    border: 5px solid #e3ecf5; border-top-color: #005495; animation: spin 0.9s linear infinite;
  }
  @keyframes spin { to { transform: rotate(360deg); } }
  h1 { margin: 0 0 8px; font-size: 18px; color: #1a3353; font-weight: 800; letter-spacing: .2px; }
  p { margin: 0 0 24px; font-size: 13px; color: #64748b; font-weight: 500; }
  .bar { position: relative; width: 100%; height: 6px; border-radius: 999px; background: #e9eff7; overflow: hidden; }
  .bar span {
    position: absolute; top: 0; left: 0; height: 100%; width: 40%; border-radius: 999px;
    background: linear-gradient(90deg, #005495, #4fa8e0); animation: slide 1.3s ease-in-out infinite;
  }
  @keyframes slide { 0% { left: -40%; } 100% { left: 100%; } }
</style>
</head>
<body>
  <div class="card">
    <div class="spinner"></div>
    <h1>${copy.title}…</h1>
    <p>${copy.subtitle}</p>
    <div class="bar"><span></span></div>
  </div>
</body>
</html>`;
}

export async function runReportJob(payload, { onStatusChange } = {}) {
  const { data: created } = await api.post('/reports/jobs', payload);
  const jobId = created.job_id;

  const startedAt = Date.now();
  while (true) {
    const { data: status } = await api.get(`/reports/jobs/${jobId}/status`);
    if (onStatusChange) onStatusChange(status.status);

    if (status.status === 'DONE') {
      return status.pdf_url;
    }
    if (status.status === 'FAILED') {
      throw new Error(status.error_message || 'Report generation failed');
    }
    if (Date.now() - startedAt > POLL_TIMEOUT_MS) {
      throw new Error('Report generation is taking too long. Please try again later.');
    }
    await new Promise(resolve => setTimeout(resolve, POLL_INTERVAL_MS));
  }
}
